import React, { useState } from 'react';

import { link } from '@aline/routes';
import { useNavigate } from 'react-router-dom';
import type { ActiveAppBar } from 'src/editor';
import { doNothing } from 'src/helpers';
import { useGetCurrentDoc } from 'src/hooks';

import { templateQuickStartSteps, workflowQuickStartSteps } from './components/helpers';
import { PageHeaderBody } from './PageHeader';
import { WorkflowCancelDialog } from '../send-for-signature';

export function getPageTitleFromActiveAppBar(
  activeAppBar: Extract<ActiveAppBar, 'template-quickstart' | 'workflow-quickstart'>
) {
  return activeAppBar === 'workflow-quickstart' ? 'New workflow' : 'Launch template';
}

export function QuickStartEditorHeader({
  activeAppBar,
}: {
  activeAppBar: Extract<ActiveAppBar, 'template-quickstart' | 'workflow-quickstart'>;
}) {
  const navigate = useNavigate();
  const { document, loading } = useGetCurrentDoc();

  switch (activeAppBar) {
    case 'template-quickstart': {
      if (loading || !document?.workflow)
        return (
          <PageHeaderBody
            activeStep={3}
            next={doNothing}
            pageTitle={getPageTitleFromActiveAppBar(activeAppBar)}
            primaryActionTitle="Continue"
            primaryDisabledReason="Loading document..."
            steps={templateQuickStartSteps}
            onClose={doNothing}
          />
        );

      return (
        <QuickStartEditorHeaderBody
          activeAppBar={activeAppBar}
          activeStep={3}
          continueLink={link.WorkflowDetailTabSummary({ workflowId: document.workflow.id })}
          steps={templateQuickStartSteps}
          workflowId={document.workflow.id}
        />
      );
    }
    case 'workflow-quickstart': {
      if (loading || !document?.workflow)
        return (
          <PageHeaderBody
            activeStep={2}
            back={() => navigate(-1)}
            next={doNothing}
            pageTitle={getPageTitleFromActiveAppBar(activeAppBar)}
            primaryActionTitle="Continue"
            primaryDisabledReason="Loading document..."
            steps={workflowQuickStartSteps}
            onClose={doNothing}
          />
        );

      return (
        <QuickStartEditorHeaderBody
          activeAppBar={activeAppBar}
          activeStep={2}
          back={() => navigate(-1)}
          continueLink={link.QuickStartPickWorkflowAction({ workflowId: document.workflow.id })}
          steps={workflowQuickStartSteps}
          workflowId={document.workflow.id}
        />
      );
    }
  }
}

function QuickStartEditorHeaderBody({
  activeAppBar,
  activeStep,
  back,
  continueLink,
  steps,
  workflowId,
}: {
  activeAppBar: Extract<ActiveAppBar, 'template-quickstart' | 'workflow-quickstart'>;
  activeStep: number;
  continueLink: string;
  steps: number[];
  workflowId: UUID;
  back?: (e: React.MouseEvent) => void;
}) {
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);

  return (
    <>
      <PageHeaderBody
        activeStep={activeStep}
        back={back}
        next={() => navigate(continueLink)}
        pageTitle={getPageTitleFromActiveAppBar(activeAppBar)}
        primaryActionTitle="Continue"
        steps={steps}
        onClose={() => setCancelOpen(true)}
      />
      <WorkflowCancelDialog open={cancelOpen} setOpen={setCancelOpen} workflowId={workflowId} />
    </>
  );
}
